import React from 'react';
import PropTypes from 'prop-types';
import { Field, FieldArray } from 'formik';
import FormikAutocompleteSelect from '../../../../components/Fields/FormikAutocompleteSelect';
import { useTranslation } from '../../../../src/i18n';
import AddAnotherField from '../../../../components/AddAnotherField';
import { getOption } from '../../../../utils/common';
import AddNewFestivalModal from '../../../../components/AddNewFestivalModal';
import NoOptionsBox from '../../../../components/NoOptionsBox';
import { TP } from '../../../../constants/consts';
import Button from '../../../../components/Button';

const FestivalsEditFields = ({
  values,
  festivalsField,
  festivalsList,
  searchFunc,
  resetOptions,
  loadingValue,
  classes,
  setFieldValue,
  mainFestival,
}) => {
  const { t } = useTranslation();
  const [newFestivalOpened, setNewFestivalState] = React.useState(false);
  const [newFestivalName, setNewFestivalName] = React.useState(null);
  const [startSearching, setSearching] = React.useState(false);
  const [festivalOptions, setFestivalOptions] = React.useState(festivalsList);

  const openAddNewFestivalModel = () => {
    setNewFestivalState(true);
  };

  const closeAddNewFestivalModal = createdFestival => {
    if (createdFestival?.id) {
      setFieldValue(newFestivalName?.fieldName, getOption(createdFestival));
    }
    setNewFestivalState(false);
    setNewFestivalName(null);
  };

  React.useEffect(() => {
    setSearching(false);
    setFestivalOptions(mainFestival ? festivalsList?.filter(f => f?.value !== mainFestival?.id) : festivalsList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [festivalsList]);

  return (
    <>
      <FieldArray name={festivalsField.name}>
        {arrayHelpers => {
          const parentName = festivalsField.name;
          const festivals = values[festivalsField.name];
          return (
            festivals.length > 0 &&
            festivals.map((festival, index) => {
              const fieldName = `${parentName}.${index}`;
              const isMainFestival = mainFestival && mainFestival?.id === festival?.value;
              return (
                <AddAnotherField
                  key={fieldName}
                  values={festivals}
                  removeFunc={() => arrayHelpers.remove(index)}
                  addFunc={() => arrayHelpers.push('')}
                  isLast={index === festivals.length - 1}
                  addDisabled={festivals.some(v => !v)}
                  className={classes.addFields}
                  isDisabled={isMainFestival}
                >
                  <Field
                    instanceId={fieldName}
                    name={fieldName}
                    component={FormikAutocompleteSelect}
                    options={festivalOptions}
                    isDisabled={isMainFestival}
                    onInputTextChange={v => {
                      setSearching(true);
                      searchFunc(fieldName, v);
                      setNewFestivalName({ fieldName, name: v });
                    }}
                    isLoading={loadingValue === fieldName}
                    label={t(festivalsField.label)}
                    onChange={() => resetOptions(parentName)}
                    onFocus={v => {
                      setSearching(true);
                      searchFunc(fieldName, v?.label);
                    }}
                    startAggregations={startSearching}
                    customComponents={{
                      NoOptionsMessage: () => (
                        <NoOptionsBox
                          buttonText={`${TP}.FN_ADD_NEW_FESTIVAL`}
                          openFormFunc={openAddNewFestivalModel}
                          t={t}
                        />
                      ),
                    }}
                    staticOption={<Button onClick={openAddNewFestivalModel}>{t(`${TP}.FN_ADD_NEW_FESTIVAL`)}</Button>}
                    isClearable
                  />
                </AddAnotherField>
              );
            })
          );
        }}
      </FieldArray>
      {newFestivalOpened && (
        <AddNewFestivalModal onClose={closeAddNewFestivalModal} festivalName={newFestivalName?.name || ''} />
      )}
    </>
  );
};

FestivalsEditFields.propTypes = {
  values: PropTypes.object.isRequired,
  festivalsField: PropTypes.object.isRequired,
  festivalsList: PropTypes.array,
  searchFunc: PropTypes.func.isRequired,
  resetOptions: PropTypes.func.isRequired,
  loadingValue: PropTypes.string,
  classes: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  mainFestival: PropTypes.object,
};

FestivalsEditFields.defaultProps = {
  festivalsList: [],
};

export default FestivalsEditFields;
